"use client";

// TokenTableRow.tsx
// Responsive row for TokenTable: icon, name, symbol, star, buy button, clickable

import React, { useMemo, useRef } from "react";
import styles from "./TokenTableRow.module.scss";
import tableColStyles from "./TableColumns.module.scss";
import Link from "next/link";
import { formatLargeNumber } from "../../utils/formatLargeNumber";
import { formatPrice } from "../../utils/formatPrice";
import { trackEvent } from '../../utils/eventTrackern';
import { getDownloadAppUrl } from '@/utils/DownloadAppLink';
import { TokenIcon } from '@/components/elements/TokenIcon';
// import { useInView } from '@/components/elements/useInView';
import { PercentageChangeBlock } from '@/components/elements/PercentageChangeBlock';
import { useRouter } from 'next/navigation';
import { generateTokenUrl } from '@/utils/tokenUrls';
import { generateStockUrl } from '@/utils/stocksUrls';
import type { Token } from '@/utils/types/token';

// Add gtag to window type for TypeScript
declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

// Define types for price and token based on the API example
// interface TokenVolume24h {
//   total?: number;
//   change?: number;
// }

interface TokenTableRowProps {
  token: Token;
  index?: number;
  windowWidth: number;
  mobileVisibleColumn: string;
  isStocks?: boolean;
}

// Responsive column visibility for TokenTableRow
// Accept windowWidth and mobileVisibleColumn as props
export const TokenTableRow: React.FC<TokenTableRowProps> = React.memo(function TokenTableRow({ token, windowWidth, mobileVisibleColumn, index = 0, isStocks = false }) {
  // Use the correct URL generator based on isStocks
  const tokenUrl = useMemo(() =>
    isStocks ? generateStockUrl(token) : generateTokenUrl(token),
    [token, isStocks]
  );

  // Price and price change
  const price = token.price?.usdPrice ?? token.price?.price;
  const priceChange = token.price?.usdChange ?? token.price?.change;

  const marketCap = useMemo(() => {
    if (token.price && typeof token.price.mc === "number") return token.price.mc;
    if (token.mc !== undefined && !isNaN(Number(token.mc))) return Number(token.mc);
    return undefined;
  }, [token.price, token.mc]);

  const liquidity = useMemo(() => {
    if (token.price && typeof token.price.liquidity === "number") return token.price.liquidity;
    if (token.liquidity !== undefined && !isNaN(Number(token.liquidity))) return Number(token.liquidity);
    return undefined;
  }, [token.price, token.liquidity]);

  const volume24h = useMemo(() => {
    if (token.price && typeof token.price.volume24h === "number") return token.price.volume24h;
    if (typeof token.volume24h === "object" && token.volume24h && typeof (token.volume24h as any).total === "number") return (token.volume24h as any).total;
    if (typeof token.volume24h === "number" ) return token.volume24h;
    return undefined;
  }, [token.price, token.volume24h]);

  const handleBuyClick = () => {
    trackEvent({
      eventName: 'BuyButton',
      eventType: 'click',
      action: 'Download',
      details: { token: token.symbol },
    });
    window.location.href = getDownloadAppUrl({
      af_channel: 'token_table_buy',
      af_sub1: token.symbol && token.mint ? `${token.symbol}+${token.mint}` : undefined,
    });
  };
 
  const rowRef = useRef<HTMLDivElement>(null);
  const router = useRouter();

  return (
    <div ref={rowRef} className={styles.row}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          window.location.href = tokenUrl;
        }
      }}
    >
      <Link
        href={tokenUrl}
        className={styles.tokenLink}
        prefetch={false}
        onMouseEnter={() => router.prefetch(tokenUrl)}
      >
        <div className={`${tableColStyles.tableCell} ${tableColStyles.tableCol} ${tableColStyles.tableColToken} ${styles.tokenCellGrid}`}>
          <TokenIcon 
            className={styles.tokenIconGrid}
            src={token.imageUri || '/tkn_lnd_pg_assets/images/missing_token_icon.svg'}
            alt={token.symbol}
            verified={token.verified}
            index={index}
            isSwiper={false}
          />
          <div className={styles.tokenContentGrid}>
          <span className={styles.tokenName}>{token.name}</span>
            <span className={styles.tokenSymbol}>{token.symbol}</span>
            
          </div>
          {price !== undefined && (
            <div className={styles.mobilePriceBlockGrid}>
              ${formatPrice(price)}
              {priceChange !== undefined && (
                <PercentageChangeBlock value={typeof priceChange === 'number' ? priceChange : 0} fontSize={12} />
              )}
            </div>
          )}
        </div>

        <div className={`${tableColStyles.tableCell} ${tableColStyles.tableCol} ${tableColStyles.tableColPrice} ${styles.priceCell} ${windowWidth < 768 && mobileVisibleColumn === 'price' ? tableColStyles.forceShow : ''}`}>
          {price !== undefined ? `$${formatPrice(price)}` : "-"}
          {priceChange !== undefined && (
            <PercentageChangeBlock value={typeof priceChange === 'number' ? priceChange : 0} fontSize={12} />
          )}
        </div>
        {/* Market Cap (>=640px) */}
        <div className={`${tableColStyles.tableCell} ${tableColStyles.tableCol} ${tableColStyles.tableColMarketCap} ${styles.marketCapCell} ${windowWidth < 768 && mobileVisibleColumn === 'marketCap' ? tableColStyles.forceShow : ''}`}>
         ${marketCap !== undefined ? formatLargeNumber(marketCap) : "-"}
        </div>
        {/* Liquidity (>=768px) */}
        <div className={`${tableColStyles.tableCell} ${tableColStyles.tableCol} ${tableColStyles.tableColLiquidity} ${styles.liquidityCell} ${windowWidth < 768 && mobileVisibleColumn === 'liquidity' ? tableColStyles.forceShow : ''}`}>
          ${liquidity !== undefined ? formatLargeNumber(liquidity) : "-"}
        </div>
        {/* Volume (>=960px) */}
        <div className={`${tableColStyles.tableCell} ${tableColStyles.tableCol} ${tableColStyles.tableColVolume} ${styles.volumeCell} ${windowWidth < 768 && mobileVisibleColumn === 'volume24h' ? tableColStyles.forceShow : ''}`}>
          ${volume24h !== undefined ? formatLargeNumber(volume24h) : "-"}
          {token.price?.volumeChange24h !== undefined && (
            <PercentageChangeBlock value={token.price.volumeChange24h} fontSize={12} />
          )}
        </div>
      </Link>
      {/* Buy button */}
      <div className={`${tableColStyles.tableCell} ${tableColStyles.tableCol} ${tableColStyles.tableColBuy} ${styles.buyCell}`}>
        <button className={styles.buyButton} onClick={handleBuyClick} aria-label={`Buy ${token.symbol}`}>Buy</button>
      </div>
    </div>
  );
});
